import React, { useState, useContext, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Link as MuiLink,
    Select,
    MenuItem,
    FormControl,
    Container,
    Dialog
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom'; // Se agregó useLocation
import logo from '../../assets/logo-viewser.png';
import loginImage from '../../assets/login-image2.webp';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../contexts/LanguageContext';
import { supabase } from '../../services/supabaseClient';

const RecuperarContraseña = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation(); // Se usa para detectar parámetros en la URL
    const { t } = useTranslation();
    const { changeLanguage, language } = useContext(LanguageContext);
    const [email, setEmail] = useState('');

    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/actualizar-contraseña`, // Cambia esto por tu URL real
        });

        if (error) {
            setError(error.message);
        } else {
            setSuccess('Se ha enviado un correo de recuperación');
        }
    };

    // Redirige al Dashboard si el usuario está autenticado
    if (user) {
        navigate('/dashboard');
    }

    // Detectamos el parámetro "confirmation" en la URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('confirmation')) {
            setOpenDialog(true);
            navigate('/login', { replace: true }); // Limpiar la URL para evitar que se muestre de nuevo
        }
    }, [location, navigate]);
    // Manejo del cambio de idioma
    const handleLanguageChange = (event) => {
        const lng = event.target.value;
        changeLanguage(lng);
    };
    return (
        <Grid container sx={{ height: '100vh', overflow: 'hidden', mt: { xs: -7, md: 0 } }}>
            {/* Columna Izquierda - Formulario */}
            <Grid
                item
                xs={12}
                md={6}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: { xs: 10, sm: 12 },
                    paddingBottom: { xs: 2, sm: 3 },
                    paddingLeft: { xs: 3, sm: 6 },
                    paddingRight: { xs: 3, sm: 6 },
                    height: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flexGrow: 1,
                    }}
                >
                    {/* Título */}
                    <Typography
                        variant="h3"
                        sx={{
                            color: '#000000',
                            textShadow: '#7894CC 1px 0 19.5px;',
                            mb: 6,
                            fontWeight: 500,
                            textAlign: 'center',
                            fontSize: { xs: '1.8rem', sm: '2.7rem' },
                            paddingLeft: { xs: 0, md: 8 },
                            paddingRight: { xs: 0, md: 8 },
                        }}
                    >
                        <Box component="span" sx={{ color: '#0F52BD' }}>
                            Viewser
                        </Box>
                        , {t('login.titleSuffix')}
                    </Typography>
                    {/* Contenedor del Formulario */}
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '500px',
                            backgroundColor: '#FFFFFF',
                            padding: { xs: 2, sm: 3 },
                            borderRadius: '8px',
                            boxShadow: '#A7BBE6 1px 0 50.5px',
                            mt: { xs: -2, md: 0 }
                        }}
                    >

                        <Typography variant='h5' sx={{ color: 'black' }}>Recuperación de contraseña</Typography>
                        <Typography variant="body2" sx={{ color: '#888888', mt: 1 }}>
                            Se enviará un mensaje de recuperación de contraseña a la dirección de correo que nos indique a continuación
                        </Typography>

                        {/* Formulario de Login */}
                        <Box component="form" onSubmit={handleSubmit} mt={1}>
                            {success && <Alert severity="success" sx={{ mb: 1, mt: 2 }}>{success}</Alert>}
                            {error && <Alert severity="error" sx={{ mb: 1, mt: 2 }}>{error}</Alert>}
                            <TextField
                                fullWidth
                                label={t('login.email')}
                                margin="dense"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />

                            {/* Botón de Enviar*/}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{
                                    borderRadius: '5px',
                                    textTransform: 'none',
                                    paddingY: 1.2,
                                    mt: 2,
                                }}
                            >
                                Enviar
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                            ¿Recuerdas tu contraseña? {' '}
                            <MuiLink component={Link} to="/login" sx={{ color: '#0F52BD', fontWeight: 'medium', textDecoration: 'none' }}>
                                {t('register.loginHere')}
                            </MuiLink>
                        </Typography>
                    </Box>
                </Box>
                {/* Logo y Enlaces Inferiores */}
                <Box
                    sx={{
                        bottom: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Box component="img" src={logo} alt="Viewser Logo" sx={{ width: '120px', mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/* Selector de Idioma */}
                        <FormControl variant="standard" sx={{ minWidth: 80 }}>
                            <Select
                                labelId="language-select-label"
                                id="language-select"
                                value={language}
                                onChange={handleLanguageChange}
                                label={t('login.language')}
                                sx={{
                                    color: '#74767A',
                                    '& .MuiSvgIcon-root': { color: '#74767A' },
                                    fontSize: '14px',
                                    minWidth: '80px',
                                }}
                            >
                                <MenuItem value="es">Español</MenuItem>
                                <MenuItem value="en">English</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Enlace de Ayuda */}
                        <MuiLink href="#" variant="body2" sx={{ color: '#74767A', textDecorationColor: '#74767A' }}>
                            {t('login.help')}
                        </MuiLink>
                        {/* Enlace de Política de Privacidad */}
                        <MuiLink href="#" variant="body2" sx={{ color: '#74767A', textDecorationColor: '#74767A' }}>
                            {t('login.privacyPolicy')}
                        </MuiLink>
                    </Box>
                </Box>
            </Grid>
            {/* Columna Derecha - Imagen */}
            <Grid
                item
                xs={false}
                md={6}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'relative',
                    height: '100%',
                }}
            >
                <Box
                    component="img"
                    src={loginImage}
                    alt="Login Image"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.8,
                    }}
                />
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen>
                <Container maxWidth="xs" sx={{ mt: 10, textAlign: 'center', padding: 3 }}>
                    <Alert severity="info">Confirma tu correo electrónico</Alert>
                </Container>
            </Dialog>

        </Grid>
    );
};
export default RecuperarContraseña;